import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, Outlet } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, ArrowUp, ChevronRight } from "lucide-react";
import { CONTACT } from "../data/content";
import { Logo as BrandLogo } from "./Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/projects", label: "Projects" },
  { to: "/qhse", label: "QHSE" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact Us" },
];

const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center group">
    <BrandLogo className="h-11 md:h-12 w-auto" />
  </Link>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0] shadow-sm" : "bg-white/60 backdrop-blur-md"}`} data-testid="site-header">
      <div className="max-w-[1360px] mx-auto px-5 md:px-8 h-[68px] flex items-center justify-between">
        <Logo />
        <nav className="hidden xl:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? "text-[#1261A0]" : "text-[#252B33] hover:text-[#1261A0]"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/contact" data-testid="header-cta" className="hidden md:inline-flex items-center gap-2 bg-[#F28C28] text-[#0B1F3A] font-semibold text-sm px-4 py-2.5 rounded-md hover:bg-[#e07d1c] hover:-translate-y-0.5 transition-transform">
            Request a Capability Discussion <ChevronRight className="h-4 w-4" />
          </Link>
          <button onClick={() => setOpen(true)} className="xl:hidden grid place-items-center h-10 w-10 rounded-md border border-[#E2E8F0] text-[#0B1F3A]" data-testid="menu-open" aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {open && createPortal(
        <div className="fixed inset-0 z-[100] xl:hidden" style={{ isolation: "isolate" }}>
          <div className="absolute inset-0 bg-[#0B1F3A]/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white p-6 flex flex-col shadow-2xl" data-testid="mobile-menu">
            <div className="flex items-center justify-between mb-8">
              <Logo />
              <button onClick={() => setOpen(false)} className="grid place-items-center h-10 w-10 rounded-md border border-[#E2E8F0]" data-testid="menu-close" aria-label="Close menu"><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex flex-col">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={({ isActive }) => `py-3 border-b border-[#F4F6F8] font-head font-semibold text-lg ${isActive ? "text-[#1261A0]" : "text-[#0B1F3A]"}`}>{n.label}</NavLink>
              ))}
            </nav>
            <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-header-cta" className="mt-6 inline-flex justify-center items-center gap-2 bg-[#F28C28] text-[#0B1F3A] font-semibold px-4 py-3 rounded-md">Request a Capability Discussion</Link>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="relative bg-[#0B1F3A] text-white overflow-hidden" data-testid="site-footer">
    <div className="absolute inset-0 tech-grid-dark opacity-40" />
    <div className="grain" />
    <div className="relative max-w-[1360px] mx-auto px-5 md:px-8 py-20">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Logo />
          <p className="mt-5 text-[#1EA7E1] font-head font-semibold text-sm tracking-wide">{CONTACT.tagline}</p>
          <p className="mt-4 text-[#94A3B8] max-w-sm leading-relaxed">Telecom infrastructure, network deployment, operations, engineering and technical workforce solutions across India.</p>
          <div className="mt-6 space-y-1.5 text-sm text-[#CBD5E1]">
            <p>{CONTACT.address}</p>
            <a href={`tel:${CONTACT.phone}`} className="block hover:text-[#1EA7E1]">{CONTACT.phone}</a>
            <a href={`mailto:${CONTACT.email}`} className="block hover:text-[#1EA7E1]">{CONTACT.email}</a>
          </div>
        </div>
        <div className="lg:col-span-3">
          <h4 className="font-head font-semibold text-sm uppercase tracking-[0.18em] text-[#1EA7E1] mb-5">Company</h4>
          <ul className="space-y-3 text-[#CBD5E1]">
            {[["About Us", "/about"], ["Services", "/services"], ["Projects", "/projects"], ["QHSE", "/qhse"], ["Careers", "/careers"], ["Contact", "/contact"]].map(([l, u]) => (
              <li key={u}><Link to={u} className="hover:text-white">{l}</Link></li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-4">
          <h4 className="font-head font-semibold text-sm uppercase tracking-[0.18em] text-[#1EA7E1] mb-5">Legal</h4>
          <ul className="space-y-3 text-[#CBD5E1]">
            {[["Privacy Policy", "/legal/privacy"], ["Terms of Use", "/legal/terms"], ["Cookie Policy", "/legal/cookies"], ["Disclaimer", "/legal/disclaimer"]].map(([l, u]) => (
              <li key={u}><Link to={u} className="hover:text-white">{l}</Link></li>
            ))}
          </ul>
          <div className="mt-6">
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#1EA7E1] hover:text-white"><MessageCircle className="h-4 w-4" /> LinkedIn / WhatsApp</a>
          </div>
        </div>
      </div>
      <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-[#94A3B8]">
        <p>© {new Date().getFullYear()} Airanet India Pvt Ltd. All rights reserved.</p>
        <p>Reg. No: {CONTACT.reg}</p>
      </div>
    </div>
  </footer>
);

const FloatingButtons = () => {
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const f = () => setShowTop(window.scrollY > 500);
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);
  return (
    <div className="fixed z-40 right-4 bottom-4 flex flex-col items-end gap-3">
      <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" data-testid="whatsapp-btn" className="grid place-items-center h-12 w-12 rounded-full bg-[#25D366] text-white shadow-lg hover:scale-105 transition-transform" aria-label="WhatsApp enquiry"><MessageCircle className="h-6 w-6" /></a>
      <a href={`tel:${CONTACT.phone}`} data-testid="call-btn" className="md:hidden grid place-items-center h-12 w-12 rounded-full bg-[#1261A0] text-white shadow-lg" aria-label="Call us"><Phone className="h-5 w-5" /></a>
      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="back-to-top" className="grid place-items-center h-12 w-12 rounded-full bg-[#0B1F3A] text-white shadow-lg hover:bg-[#1261A0]" aria-label="Back to top"><ArrowUp className="h-5 w-5" /></button>
      )}
    </div>
  );
};

const CookieBanner = () => {
  const [show, setShow] = useState(false);
  useEffect(() => { setShow(!localStorage.getItem("airanet_cookie")); }, []);
  if (!show) return null;
  return (
    <div className="fixed z-40 left-4 bottom-4 max-w-sm bg-white border border-[#E2E8F0] rounded-lg shadow-xl p-5" data-testid="cookie-banner">
      <p className="text-sm text-[#252B33]">We use cookies to improve your experience. See our <Link to="/legal/cookies" className="text-[#1261A0] underline">Cookie Policy</Link>.</p>
      <div className="mt-3 flex gap-2">
        <button onClick={() => { localStorage.setItem("airanet_cookie", "1"); setShow(false); }} data-testid="cookie-accept" className="bg-[#0B1F3A] text-white text-sm px-4 py-2 rounded-md">Accept</button>
        <button onClick={() => setShow(false)} className="text-sm px-4 py-2 rounded-md border border-[#E2E8F0]">Dismiss</button>
      </div>
    </div>
  );
};

export default function Layout() {
  const loc = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-[68px]"><Outlet /></main>
      <Footer />
      <FloatingButtons />
      <CookieBanner />
    </div>
  );
}
